import React, {
  useCallback, useState,
  useEffect,
} from 'react';
import { SELECT_MODAL_STEPS, optionsList } from './useDnsModal.types';
import useLocale from '@/hooks/useLocale';
import { useDispatch, useSelector } from 'react-redux';
import { sitesActions } from '@/redux/modules/sites';
import { DomainOriginalDestinationStep } from './components/DomainOriginalDestinationStep';
import { HowToSetupStep } from './components/HowToSetupStep';
import { FinishDomainSetupStep } from './components/FinishDomainSetupStep';
import {
  Button, IconAtencion, IconIlustratedGlobal, Modal,
} from 'gatorcomponents';
import { CART_URL } from '@/config';
import { DomainNotRegistered } from './components/DomainNotRegistered';
import * as Styles from './useDnsModal.styles';
import { ATIVACAO_GET_DNS_FROM_ICANN } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { DisclaimerExternalDomain } from './components/DisclaimerExternalDomain';
import { useResize } from '@/hooks/useResize';

export const UseDnsModal = ({
  initialStep = SELECT_MODAL_STEPS.CHECKING_STATUS,
  onClose,
  domain = 'domainSample.com',
  handleActivateDomain,
  displayActivateErrorModal,
  cloudflare,
  sites,
  clearCloudFlareLastActivation,
}) => {
  const failedActivation = cloudflare.lastActivation.status === 'failure';
  const domainWithProblem = failedActivation && cloudflare.lastActivation.message === 'frozen domain';
  const { useDnsModal: useDnsModalLocale } = useLocale();
  const { manualSelectDns: manualSelectDnsLocale } = useDnsModalLocale;

  const dispatch = useDispatch();
  const getDnsFromIcann = useFeatureIsOn(ATIVACAO_GET_DNS_FROM_ICANN);
  const { loading: sitesLoading, domainDns } = useSelector(state => state.sites);
  const [selectManualDnsStep, setSelectManualDnsStep] = useState(initialStep);
  const [selectedManualDns, setSelectedManualDns] = useState(null);
  const [canProceed, setCanProceed] = useState(false);

  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 600;

  const onDomainNotRegistered = () => {
    setSelectManualDnsStep(SELECT_MODAL_STEPS.DOMAIN_NOT_REGISTERED);
  };

  const requestDomainDns = useCallback((domain) => {
    dispatch(sitesActions.domainDns.request({ domain, onDomainNotRegistered, getDnsFromIcann }));
  }, [dispatch, getDnsFromIcann]);

  const requestPlanDns = useCallback(planId => dispatch(sitesActions.planDns.request(planId)), [dispatch]);

  const setSelectManualDnsNextStep = useCallback(({ nextStep }) => {
    if (nextStep) {
      setSelectManualDnsStep(nextStep);
    }

    if (!nextStep) {
      setSelectManualDnsStep(+1);
    }
  }, []);

  const handleCloudflareLoaded = useCallback(() => {
    const getDnsSuccess = sites.loadingRedirect === false && sites.redirectStatus === 'success';

    if (cloudflare.lastActivation.cloudflare_ns1 && getDnsSuccess && selectedManualDns && canProceed) {
      setSelectManualDnsNextStep({ nextStep: SELECT_MODAL_STEPS.HOW_TO_SETUP });
    }
  }, [cloudflare, setSelectManualDnsNextStep, sites.loadingRedirect, sites.redirectStatus, selectedManualDns, canProceed]);

  useEffect(() => {
    handleCloudflareLoaded();
  }, [handleCloudflareLoaded]);

  const handleClose = useCallback(() => {
    onClose && onClose();
    setSelectManualDnsStep(SELECT_MODAL_STEPS.CHECKING_STATUS);
    setSelectedManualDns(null);
    setCanProceed(false);

    if (cloudflare.lastActivation.requestStatus === 'success' || cloudflare.lastActivation.status === 'success') {
      clearCloudFlareLastActivation && clearCloudFlareLastActivation();
    }
  }, [
    onClose,
    clearCloudFlareLastActivation,
    cloudflare.lastActivation.requestStatus,
    cloudflare.lastActivation.status,
  ]);

  const handleDisplayActivateErrorModal = useCallback(() => {
    if (cloudflare.lastActivation && cloudflare.lastActivation.status === 'failure') {
      displayActivateErrorModal(cloudflare.lastActivation);
      setSelectManualDnsStep(SELECT_MODAL_STEPS.CHECKING_STATUS);
      setSelectedManualDns(null);
      onClose();
    }
  }, [cloudflare.lastActivation, onClose, displayActivateErrorModal]);

  useEffect(() => {
    handleDisplayActivateErrorModal();
  }, [handleDisplayActivateErrorModal]);

  const getDomainOriginalDestinationStepProps = useCallback(({ domain }) => {
    const handleContinue = () => {
      handleActivateDomain();
      setCanProceed(true);
    };

    return (
      {
        subtitle: manualSelectDnsLocale.step.domainOriginalRegistrationV2.subtitle,
        alertDescription: manualSelectDnsLocale.step.domainOriginalRegistration.alertDescription,
        domain,
        optionsList,
        selectedManualDns,
        setSelectedManualDns,
        backLabel: manualSelectDnsLocale.backLabel,
        continueLabel: manualSelectDnsLocale.continueLabel,
        onHowToSetupClick: handleContinue,
        onCancelClick: handleClose,
        selectPlaceholder: manualSelectDnsLocale.step.domainOriginalRegistrationV2.selectPlaceholder,
        loading: cloudflare.loadingActivation,
      }
    );
  }, [
    manualSelectDnsLocale,
    selectedManualDns,
    handleClose,
    handleActivateDomain,
    cloudflare,
  ]);

  const getDisclaimerExternalDomainProps = useCallback(({ domain }) => ({
    title: manualSelectDnsLocale.step.disclaimerExternalDomain.title,
    subtitle: manualSelectDnsLocale.step.disclaimerExternalDomain.subtitle({ domain }),
    onClickCta: () => requestDomainDns(domain),
    cta: manualSelectDnsLocale.step.disclaimerExternalDomain.cta,
    loading: sites.loading,
  }), [
    manualSelectDnsLocale,
    requestDomainDns,
    sites.loading,
  ]);

  const getHowToSetupProps = useCallback(() => ({
    subtitle: manualSelectDnsLocale.step.howToSetupV2.subtitle,
    selectedManualDns,
    onClickBack: () => setSelectManualDnsNextStep({ nextStep: SELECT_MODAL_STEPS.GET_DOMAIN_ORIGINAL_REGISTRATION }),
    onHowToSetupClick: () => setSelectManualDnsNextStep({ nextStep: SELECT_MODAL_STEPS.FINISH_SETUP_STEP }),
    serverLabel: index => manualSelectDnsLocale.step.howToSetupV2.serverLabel(index),
    continueLabel: manualSelectDnsLocale.continueLabel,
    originalDnsLabel: manualSelectDnsLocale.step.howToSetupV2.originalDnsLabel,
    newDnsLabel: manualSelectDnsLocale.step.howToSetupV2.newDnsLabel,
    configureDomainText: manualSelectDnsLocale.step.howToSetupV2.configureDomainText,
    configureDomainButton: manualSelectDnsLocale.step.howToSetupV2.configureDomainButton,
    domainHasConfiguredLabel: manualSelectDnsLocale.step.howToSetupV2.domainHasConfiguredLabel,
    backLabel: manualSelectDnsLocale.backLabel,
    domainDns: {
      ns1: domainDns ? domainDns[0] : null,
      ns2: domainDns ? domainDns[1] : null,
    },
    cloudflareDns: {
      ns1: cloudflare.lastActivation.cloudflare_ns1,
      ns2: cloudflare.lastActivation.cloudflare_ns2,
    },
    originalDns: {
      ns1: cloudflare.lastActivation.original_ns1,
      ns2: cloudflare.lastActivation.original_ns2,
    },
    helpLink: manualSelectDnsLocale.helpLink,
  }), [
    manualSelectDnsLocale,
    domainDns,
    selectedManualDns,
    setSelectManualDnsNextStep,
    cloudflare,
  ]);

  const getDomainNotRegistered = useCallback(({ domain }) => {
    const newDomain = domain.split('.');
    const goTo = () => {
      window.open(`${CART_URL}?pid=d&sld=${newDomain[0]}&tld=.${newDomain[1]}`, '_blank');
    };

    return {
      title: manualSelectDnsLocale.step.domainNotRegistered.title(domain),
      description: manualSelectDnsLocale.step.domainNotRegistered.description,
      redirectLabel: manualSelectDnsLocale.step.domainNotRegistered.redirectLabel,
      goTo,
    };
  }, [
    manualSelectDnsLocale,
  ]);

  const getFinishDomainSetupStep = useCallback(() => {
    const onGoBack = () => {
      setSelectManualDnsNextStep({ nextStep: SELECT_MODAL_STEPS.HOW_TO_SETUP });
      setCanProceed(false);
    };

    return {
      title: manualSelectDnsLocale.step.finishDomainSetupV2.title,
      subtitle: manualSelectDnsLocale.step.finishDomainSetupV2.subtitle,
      box: manualSelectDnsLocale.step.finishDomainSetupV2.box,
      howToSectionTitle: manualSelectDnsLocale.step.finishDomainSetupV2.howToSectionTitle,
      onGoBack,
      onClose: handleClose,
      from: selectedManualDns,
      finishLabel: manualSelectDnsLocale.finishLabel,
      backLabel: manualSelectDnsLocale.backLabel,
    };
  }, [
    handleClose,
    manualSelectDnsLocale,
    selectedManualDns,
    setSelectManualDnsNextStep,
  ]);

  const domainWithProblemContent = () => (
    <Styles.Container id="activateDeactivateModalContainer">
      <IconAtencion secondaryColor="problemLight" size="xl" />

      <Styles.Title data-testid="title">
        {manualSelectDnsLocale.step.activationError.domainWithProblemTitle}
      </Styles.Title>

      <Styles.Description data-testid="description">
        {manualSelectDnsLocale.step.activationError.domainWithProblemDescription}
      </Styles.Description>

      <Styles.ActionsRow>
        <Button
          size="large"
          testId="confirmation"
          label={manualSelectDnsLocale.step.activationError.confirmationCta}
          onClick={handleClose}
        />
      </Styles.ActionsRow>
    </Styles.Container>
  );

  const selectManualDnsModal = () => {
    const steps = {
      [SELECT_MODAL_STEPS.CHECKING_STATUS]: (
        <Modal
          onClose={handleClose}
          maxHeight="unset"
          maxWidth={960}
          testId="disclaimer-external-domain-modal"
        >
          <DisclaimerExternalDomain
            {...getDisclaimerExternalDomainProps({ domain })}
          />
        </Modal>
      ),
      [SELECT_MODAL_STEPS.GET_DOMAIN_ORIGINAL_REGISTRATION]: (
        <Modal
          onClose={handleClose}
          maxHeight="unset"
          maxWidth={960}
          testId="select-dns-modal"
          mobileTitleInNewLine
          title={(
            <Styles.ModalTitle>
              <IconIlustratedGlobal />
              {` ${manualSelectDnsLocale.titleV2}`}
            </Styles.ModalTitle>
          )}
        >
          <DomainOriginalDestinationStep
            {...getDomainOriginalDestinationStepProps({ domain })}
          />
        </Modal>
      ),
      [SELECT_MODAL_STEPS.HOW_TO_SETUP]: (
        <Modal
          onClose={handleClose}
          maxHeight="unset"
          maxWidth={960}
          testId="select-dns-modal"
          mobileTitleInNewLine
          noContentPadding={mobile}
          title={(
            <Styles.ModalTitle>
              <IconIlustratedGlobal />
              {` ${manualSelectDnsLocale.titleV2}`}
            </Styles.ModalTitle>
          )}
        >
          <HowToSetupStep
            {...getHowToSetupProps()}
          />
        </Modal>
      ),
      [SELECT_MODAL_STEPS.FINISH_SETUP_STEP]: (
        <Modal
          onClose={handleClose}
          maxHeight="unset"
          maxWidth={674}
          testId="select-dns-modal"
        >
          <FinishDomainSetupStep
            {...getFinishDomainSetupStep({ domain })}
          />
        </Modal>
      ),
      [SELECT_MODAL_STEPS.DOMAIN_NOT_REGISTERED]: (
        <Modal
          onClose={handleClose}
          maxHeight="unset"
          maxWidth={960}
          testId="select-dns-modal"
        >
          <DomainNotRegistered
            {...getDomainNotRegistered({ domain })}
          />
        </Modal>
      ),
      [SELECT_MODAL_STEPS.DOMAIN_WITH_PROBLEM]: (
        <Modal
          onClose={handleClose}
          testId="modal-domain-with-problem"
        >
          {domainWithProblemContent()}
        </Modal>
      ),
    };

    if (domainWithProblem) {
      return steps[SELECT_MODAL_STEPS.DOMAIN_WITH_PROBLEM];
    }

    return steps[selectManualDnsStep];
  };

  const useDnsModalData = {
    selectManualDnsModal,
    requestDomainDns,
    sitesLoading,
    selectManualDnsStep,
    setSelectManualDnsStep,
    setSelectManualDnsNextStep,
    requestPlanDns,
    domainDns,
  };

  return useDnsModalData;
};

export default UseDnsModal;
